import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react"
import {
  PersonalAssistantMessage as PersonalAssistantMessageType,
  upsertPersonalAssistantMessageOnDb,
} from "@database/personalAssistantMessages/savePersonalAssistantMessage"
import { getLatestPersonalAssistantMessage } from "@database/personalAssistantMessages/getPersonalAssistantMessage"
import { deserialize } from "@app/helpers/database/setup/utils"
import { useSession } from "next-auth/react"

interface GlobalContextProps {
  personalAssistantMessage: PersonalAssistantMessageType
  setPersonalAssistantMessage: React.Dispatch<
    React.SetStateAction<PersonalAssistantMessageType>
  >
  updatePersonalAssistantMessage: (
    newPersonalAssistantMessage: Partial<PersonalAssistantMessageType>,
  ) => Promise<void>
  loadingPersonalAssistantMessage: boolean
}

const GlobalContext = createContext<GlobalContextProps | undefined>(undefined)

interface GlobalProviderProps {
  children: ReactNode
}

const fetchLatestPersonalAssistantMessageTimeout = 10000

// Module-level singleton to prevent multiple intervals across all instances
const globalIntervalState = {
  intervalId: null as NodeJS.Timeout | null,
  timeoutId: null as number | null,
  idleCallbackId: null as number | null,
  currentEmail: null as string | null,
  isFetching: false, // Track if a fetch is currently in progress
}

export const PersonalAssistantMessageContext: React.FC<GlobalProviderProps> = ({
  children,
}) => {
  const [personalAssistantMessage, setPersonalAssistantMessage] =
    useState<PersonalAssistantMessageType | null>(null)
  const [loadingPersonalAssistantMessage, setLoadingPersonalAssistantMessage] =
    useState(false)
  const { data: session, status } = useSession()

  const currentVersionRef = useRef<number>(0)
  const fetchFnRef = useRef<(() => Promise<void>) | null>(null)

  const fetchLatestPersonalAssistantMessage = useCallback(async () => {
    if (
      !session?.user ||
      status === "loading" ||
      status === "unauthenticated"
    ) {
      return
    }

    // Prevent concurrent fetches - if one is already in progress, skip
    if (globalIntervalState.isFetching) {
      return
    }

    try {
      globalIntervalState.isFetching = true
      setLoadingPersonalAssistantMessage(true)

      const latestPersonalAssistantMessage =
        deserialize<PersonalAssistantMessageType | null>(
          await getLatestPersonalAssistantMessage(
            undefined,
            currentVersionRef.current > 0
              ? currentVersionRef.current
              : undefined,
          ),
        )
      if (
        latestPersonalAssistantMessage?.message &&
        (latestPersonalAssistantMessage.version === 0 ||
          latestPersonalAssistantMessage.version > currentVersionRef.current)
      ) {
        // console.log(
        //   "Received new personal assistant message with version:",
        //   latestPersonalAssistantMessage.version,
        //   "and context:",
        //   latestPersonalAssistantMessage.context,
        // )
        await setPersonalAssistantMessage(latestPersonalAssistantMessage)
      } else {
        console.warn("Can't fetch personal assistant message from database.")
      }
    } catch (error) {
      console.error("Failed to fetch personal assistant message:", error)
    } finally {
      globalIntervalState.isFetching = false
      setLoadingPersonalAssistantMessage(false)
    }
  }, [session?.user, status])

  // Store the function in a ref so we can use it in useEffect without it being a dependency
  fetchFnRef.current = fetchLatestPersonalAssistantMessage

  useEffect(() => {
    // Only run when session is available
    if (status === "loading") return
    if (status === "unauthenticated") return

    const currentEmail = session?.user?.email

    // If email changed, reset everything
    if (
      currentEmail &&
      globalIntervalState.currentEmail !== null &&
      globalIntervalState.currentEmail !== currentEmail
    ) {
      // Clear existing intervals/timeouts
      if (globalIntervalState.timeoutId !== null) {
        clearTimeout(globalIntervalState.timeoutId)
        globalIntervalState.timeoutId = null
      }
      if (globalIntervalState.idleCallbackId !== null) {
        cancelIdleCallback(globalIntervalState.idleCallbackId)
        globalIntervalState.idleCallbackId = null
      }
      if (globalIntervalState.intervalId !== null) {
        clearInterval(globalIntervalState.intervalId)
        globalIntervalState.intervalId = null
      }
    }

    // Clear any pending timeouts/idle callbacks before scheduling new ones
    if (globalIntervalState.timeoutId !== null) {
      clearTimeout(globalIntervalState.timeoutId)
      globalIntervalState.timeoutId = null
    }
    if (globalIntervalState.idleCallbackId !== null) {
      cancelIdleCallback(globalIntervalState.idleCallbackId)
      globalIntervalState.idleCallbackId = null
    }

    if (currentEmail) {
      globalIntervalState.currentEmail = currentEmail

      // Defer personal assistant message fetch until after initial render to improve page load performance
      // Use requestIdleCallback if available, otherwise fallback to setTimeout
      if (typeof window !== "undefined") {
        if ("requestIdleCallback" in window && window.requestIdleCallback) {
          globalIntervalState.idleCallbackId = window.requestIdleCallback(
            () => {
              // Use ref to avoid stale closure
              if (fetchFnRef.current) {
                fetchFnRef.current()
              }

              // Fetch regularly every 10 seconds
              globalIntervalState.intervalId = setInterval(() => {
                // Prevent concurrent fetches
                if (globalIntervalState.isFetching) {
                  return
                }
                if (fetchFnRef.current) {
                  fetchFnRef.current()
                }
              }, fetchLatestPersonalAssistantMessageTimeout)
            },
            { timeout: 2000 },
          )
        } else {
          // Fallback for browsers without requestIdleCallback
          const timeoutId = setTimeout(() => {
            // Use ref to avoid stale closure
            if (fetchFnRef.current) {
              fetchFnRef.current()
            }
            // Fetch regularly every 10 seconds
            globalIntervalState.intervalId = setInterval(() => {
              // Prevent concurrent fetches
              if (globalIntervalState.isFetching) {
                return
              }
              if (fetchFnRef.current) {
                fetchFnRef.current()
              }
            }, fetchLatestPersonalAssistantMessageTimeout)
          }, 100) // Small delay to allow initial render
          globalIntervalState.timeoutId = Number(timeoutId)
        }
      }
    }

    // Cleanup function
    return () => {
      // Always clear pending timeouts and idle callbacks on unmount
      if (globalIntervalState.timeoutId !== null) {
        clearTimeout(globalIntervalState.timeoutId)
        globalIntervalState.timeoutId = null
      }
      if (globalIntervalState.idleCallbackId !== null) {
        cancelIdleCallback(globalIntervalState.idleCallbackId)
        globalIntervalState.idleCallbackId = null
      }

      // Only clear interval if email changed or session lost
      // Don't clear on React Strict Mode remounts (where email hasn't changed)
      const emailChanged =
        currentEmail !== null &&
        globalIntervalState.currentEmail !== null &&
        currentEmail !== globalIntervalState.currentEmail
      const sessionLost =
        !currentEmail && globalIntervalState.currentEmail !== null

      if (emailChanged || sessionLost) {
        if (globalIntervalState.intervalId !== null) {
          clearInterval(globalIntervalState.intervalId)
          globalIntervalState.intervalId = null
        }
        if (sessionLost) {
          globalIntervalState.currentEmail = null
        }
      }
      // Note: We intentionally don't clear the interval on unmount if email hasn't changed
      // This allows the interval to persist across React Strict Mode remounts
    }
  }, [session?.user?.email, status]) // Removed fetchLatestPersonalAssistantMessage from dependencies

  useEffect(() => {
    if (personalAssistantMessage) {
      currentVersionRef.current = personalAssistantMessage.version
    }
  }, [personalAssistantMessage])

  const updatePersonalAssistantMessage = useCallback(
    async (
      newPersonalAssistantMessage: Partial<PersonalAssistantMessageType>,
    ) => {
      await upsertPersonalAssistantMessageOnDb(newPersonalAssistantMessage)
    },
    [],
  )

  return (
    <GlobalContext.Provider
      value={{
        personalAssistantMessage,
        setPersonalAssistantMessage,
        updatePersonalAssistantMessage,
        loadingPersonalAssistantMessage,
      }}
    >
      {children}
    </GlobalContext.Provider>
  )
}

// Custom hook for accessing the global context
export const usePersonalAssistantMessageContext = () =>
  useContext(GlobalContext)
